import { Stack } from "@mui/material";
import type { FC } from "react";
import {
	type ClientPortfolioDetailInvestmentResponseDTO,
	ClientPortfolioInvestmentEnterFeeAmountTypeEnum,
	ClientPortfolioInvestmentEnterFeeApplyingEnum,
	ClientPortfolioInvestmentEnterFeeTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { DataRowCollapsible } from "web_myplann/Shared/Components/DataRowCollapsible";
import {
	dateFormatter,
	moneyFormatter,
} from "web_myplann/Shared/Utils/formatters";

type Props = {
	investments: ClientPortfolioDetailInvestmentResponseDTO[];
};

export const FundSection: FC<Props> = ({ investments }) => {
	const { t } = useResource();

	const colapseRowLabel = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) =>
		`${t(Resources.PortfolioDetail.ToolHistory.ActionType[investment.investmentType ?? ClientPortfolioInvestmentTypeEnum.Buy])} - ${dateFormatter(investment.startAt)}`;

	const frequencyValue = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) =>
		`${moneyFormatter(investment.amount, t(Resources.PortfolioDetail.SharedLabels.Currency[investment.currency]))} ${t(Resources.PortfolioDetail.ToolHistory.Fund.Frequency[investment.frequency ?? ClientPortfolioInvestmentFrequencyEnum.Monthly]).toLowerCase()}`;

	const enterFeeValue = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) => {
		if (
			investment.enterFee?.amountType ===
			ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Percent
		) {
			return `${investment.enterFee?.amount}%`;
		}

		if (
			investment.enterFee?.amountType ===
			ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Amount
		) {
			return moneyFormatter(
				investment.enterFee?.amount,
				t(Resources.PortfolioDetail.SharedLabels.Currency[investment.currency]),
			);
		}

		return "";
	};

	const chargedFeeFromFirstPayments = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) => {
		if (investment.enterFee?.chargedFeeFromFirstPayments) {
			return `${investment.enterFee?.chargedFeeFromFirstPayments}%`;
		}
		return "";
	};

	const transactionTypeValue = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) =>
		investment.transactionType
			? t(
					Resources.PortfolioDetail.ToolHistory.Fund.TransactionType[
						investment.transactionType
					],
				)
			: "";

	const displayApplying = (
		investment: ClientPortfolioDetailInvestmentResponseDTO,
	) => {
		if (
			investment.enterFee?.type ===
			ClientPortfolioInvestmentEnterFeeTypeEnum.FromFirstPayments
		) {
			return false;
		}

		if (
			investment.enterFee?.type ===
			ClientPortfolioInvestmentEnterFeeTypeEnum.FromEveryPayment
		) {
			return false;
		}

		if (
			investment.enterFee?.type ===
			ClientPortfolioInvestmentEnterFeeTypeEnum.Once
		) {
			if (
				investment.frequency === ClientPortfolioInvestmentFrequencyEnum.Once
			) {
				return true;
			}

			return false;
		}

		return true;
	};

	return (
		<Stack>
			{investments.map((investment, index) => {
				return (
					<DataRowCollapsible
						key={investment.id}
						label={colapseRowLabel(investment)}
						defaultExpanded={index === 0}
					>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.type)}
							value={t(
								Resources.PortfolioDetail.ToolHistory.ActionType[
									investment.investmentType ??
										ClientPortfolioInvestmentTypeEnum.Buy
								],
							)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.startDate)}
							value={dateFormatter(investment.startAt)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.endDate)}
							value={dateFormatter(investment.endAt)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.amount)}
							value={frequencyValue(investment)}
						/>
						<DataRow
							label={t(
								Resources.PortfolioDetail.ToolHistory.Fund.transactionType,
							)}
							value={transactionTypeValue(investment)}
						/>
						{investment.otherDisposition && (
							<DataRow
								label={t(
									Resources.PortfolioDetail.ToolHistory.Fund.disposition,
								)}
								value={investment.otherDisposition}
							/>
						)}
						{investment.enterFee && (
							<>
								<DataRow
									label={t(
										Resources.PortfolioDetail.ToolHistory.Fund.entryTransaction,
									)}
									value={t(
										Resources.PortfolioDetail.ToolHistory.Fund.EntryTransaction[
											investment.enterFee?.type ??
												ClientPortfolioInvestmentEnterFeeTypeEnum.Once
										],
									)}
								/>
								<DataRow
									label={t(
										Resources.PortfolioDetail.ToolHistory.Fund
											.entryTransactionAmount,
									)}
									value={enterFeeValue(investment)}
								/>

								{displayApplying(investment) && (
									<DataRow
										label={t(
											Resources.PortfolioDetail.ToolHistory.Fund.applying,
										)}
										value={t(
											Resources.PortfolioDetail.ToolHistory.Fund.Applying[
												investment.enterFee?.applying ??
													ClientPortfolioInvestmentEnterFeeApplyingEnum.ChargedFeeFromInvestment
											],
										)}
									/>
								)}
								{investment.enterFee?.type ===
									ClientPortfolioInvestmentEnterFeeTypeEnum.FromFirstPayments && (
									<DataRow
										label={t(
											Resources.PortfolioDetail.ToolHistory.Fund
												.entryTransactionProcantage,
										)}
										value={chargedFeeFromFirstPayments(investment)}
									/>
								)}
							</>
						)}
					</DataRowCollapsible>
				);
			})}
		</Stack>
	);
};
